"use client";

import {memo, ReactNode, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useSnapshot} from "valtio";

import {LocationSearchView} from "@/features/locations";
import {type PropertyFiltersInstance} from "@/features/rental";
import {AdvancedFiltersResponsive} from "@/features/rental/advanced-filters";
import {DateFlexibilityView} from "@/features/rental/date-flexibility";
import {GuestsView} from "@/features/rental/guests";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import "./styles/property-filters.scss";

interface PropertyFiltersViewProps {
    model: PropertyFiltersInstance;
    children?: ReactNode;
    className?: string;
}

export const PropertyFiltersView = memo(function PropertyFiltersView({model, className, children}: PropertyFiltersViewProps) {
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const advancedFiltersSnap = useSnapshot(model.advancedFilters);
    const activeFiltersCount = advancedFiltersSnap.activeFiltersCount;

    const {t} = useTranslation("search");
    const handleCloseFilters = (): void => {
        setIsAdvancedFiltersOpen(false);
    };
    return (
        <div className={`property-filters ${className || ""}`}>
            <div className="property-filters__fields">
                <LocationSearchView
                    dropdownOffset={2}
                    icon={<SmartImage src={getStaticPathIcon("property/location")} width={20} height={20} alt="" />}
                    model={model.locationSearch}
                    placeholder={t("searchDestinations")}
                />

                <DateFlexibilityView model={model.dateFlexibility} />

                <GuestsView placement="bottom" model={model.guests} showResetButton={true} dropdownOffset={5} />

                {/* Advanced Filters - Adaptive (Desktop: Dropdown, Mobile: Modal) */}
                <AdvancedFiltersResponsive
                    model={model.advancedFilters}
                    isOpen={isAdvancedFiltersOpen}
                    onClose={handleCloseFilters}
                    placement="bottom-start"
                    trigger={
                        <ButtonBase
                            variant="secondary"
                            onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
                            className="property-filters__filters-btn"
                        >
                            <SmartImage src={getStaticPathIcon("filters")} width={20} height={20} alt="Filters" />
                            {activeFiltersCount > 0 && <span className="property-filters__filters-count">({activeFiltersCount})</span>}
                            {t("$.filters.filters")}
                        </ButtonBase>
                    }
                />
                {children}
            </div>
        </div>
    );
});
